/* eslint-disable complexity */
import { CUTOFF_DAYS_DEFAULT, CUTOFF_DAYS_COVID_VACCINATION } from 'constants/booking'

import { useEffect, useState } from 'react'
import T from 'prop-types'
import { useHistory, Link } from 'react-router-dom'
import { addDays, format } from 'date-fns'
import Confirm from 'components/Confirm'
import Container from 'components/Container'
import TimeTable from 'components/TimeTable'
import Message from 'components/Message'
import ErrorMessage from 'components/ErrorMessage'
import Spinner from 'components/Spinner'
import useSWR from 'swr'
import { getAvailability, editBookingSlotId, getVaccination, getSlot } from 'lib/requests'
import { get } from 'lodash'
import { uppercaseFirst } from 'lib/utils'
import { formatDate, formatDateClean } from 'lib/date'
import useSession from 'hooks/useSession'
import { getJSON } from 'lib/storage'
import Step from 'apps/booking/components/Step'
import Header from 'apps/booking/components/Header'

const formatFullDate = ({ start_time }) => {
  if (!start_time) {
    return null
  }

  const fullDate = uppercaseFirst(formatDate(start_time, 'EEEE d. MMMM'))
  const time = format(start_time, 'HH:mm')

  return `${fullDate}, ${time}`
}

export default function EditBooking({
  match: {
    params: { orgUnit, vaccinationId, bookingTypeId },
  },
}) {
  const { session } = useSession()
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [locationId, setLocationId] = useState(null)
  const [vaccination, setVaccination] = useState(null)
  const [bookingError, setBookingError] = useState(null)
  const [loadingInitialData, setLoadingInitialData] = useState(true)
  const [initialDataError, setInitialDataError] = useState(null)

  useEffect(() => {
    const getData = async () => {
      try {
        const vaccination = await getVaccination(orgUnit, vaccinationId)
        const slot = await getSlot(vaccination.booking.slot_id)
        setLocationId(slot.location.id)
        setVaccination(vaccination)
      } catch (e) {
        setInitialDataError(
          'Kunne ikke hente informasjon om vaksinasjonstimen, vennligst prøv igjen.',
        )
      } finally {
        setLoadingInitialData(false)
      }
    }

    getData()
  }, [orgUnit, vaccinationId])

  // Use a different cutoff time for covid vaccine
  const CUTOFF_DAYS =
    bookingTypeId === 'COVID_VACCINE' ? CUTOFF_DAYS_COVID_VACCINATION : CUTOFF_DAYS_DEFAULT

  // Offset start date to ensure > 6 months between dose 2 and 3
  const { allowDose3BookingFromDate } = getJSON('bookingState')
  const dose3BookingOffset =
    bookingTypeId === 'COVID_VACCINE' && allowDose3BookingFromDate
      ? allowDose3BookingFromDate
      : null
  const { data, loading: loadingAvailability } = useSWR(
    [
      format(dose3BookingOffset ? new Date(dose3BookingOffset) : new Date(), 'yyyy-MM-dd'),
      format(
        addDays(dose3BookingOffset ? new Date(dose3BookingOffset) : new Date(), CUTOFF_DAYS),
        'yyyy-MM-dd',
      ),
      orgUnit,
      bookingTypeId,
    ],
    getAvailability,
    { refreshInterval: 10 * 1000 },
  )

  const [confirm, setConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const history = useHistory()

  const { first_name, last_name } = session || {}
  const firstName = vaccination?.booking?.details?.on_behalf_of?.first_name ?? first_name
  const lastName = vaccination?.booking?.details?.on_behalf_of?.last_name ?? last_name
  const fullName = `${firstName} ${lastName}`

  const handleSelect = (slot) => {
    setBookingError(null)
    setSelectedSlot(slot)
    setConfirm(true)
  }

  const handleAbort = () => {
    setConfirm(false)
    setBookingError(null)
  }

  const handleConfirm = async () => {
    setBookingError(null)
    setLoading(true)
    const newSlotId = selectedSlot.id
    const bookingId = vaccination?.booking.id
    const slotId = vaccination.booking?.slot_id
    const response = await editBookingSlotId(slotId, bookingId, newSlotId)

    if (response.error) {
      setLoading(false)
      setBookingError(response.error)
      return
    }

    setTimeout(() => {
      history.push(`/${orgUnit}/bekreftelse?s=${newSlotId}&n=${fullName}&type=${bookingTypeId}`)
    }, 2000)
  }

  if (loadingInitialData || loadingAvailability) {
    return (
      <Wrapper>
        <Spinner center full box={false} />
      </Wrapper>
    )
  }

  if (initialDataError) {
    return (
      <Wrapper>
        <Message type="warning">{initialDataError}</Message>
      </Wrapper>
    )
  }

  const selectedDate = selectedSlot ? formatFullDate(selectedSlot) : ''
  const slotFull = get(bookingError, 'status') === 409
  const location = data?.find(({ id }) => id === locationId)

  return (
    <Wrapper>
      <Step title="Velg nytt tidspunkt">
        <Message type="notice">
          Pass på at du bestiller time med korrekt mellomrom mellom dosene. <br />
          <br />
          Det er ulike anbefalinger for ulike personer. Du finner de{' '}
          <a
            href="https://www.fhi.no/sv/vaksine/koronavaksinasjonsprogrammet/koronavaksine/#om-vaksinering"
            target="_blank"
            rel="noreferrer"
            style={{ textOverflow: 'ellipsis' }}
          >
            nøyaktige anbefalingene her.
          </a>
          <br />
        </Message>
        {!data && <Spinner full center box={false} />}

        {/* No available dates */}
        {data && data.length === 0 && (
          <Message type="notice">
            Det er ingen ingen tilgjengelige timer i perioden fra{' '}
            {formatDateClean(
              dose3BookingOffset ? new Date(dose3BookingOffset) : new Date(),
              'd. MMMM yyyy',
            )}
            .
            <br />
            <br />
            Ta kontakt med ditt vaksinekontor om du mener dette er feil. Du kan også{' '}
            <Link to={`/${orgUnit}`}>gå tilbake til start og prøve igjen</Link>.
          </Message>
        )}

        <TimeTable data={location?.dates || []} onSelect={handleSelect} />

        <Confirm
          title={slotFull ? 'Velg et annet tidspunkt' : `Ny tid: ${selectedDate}`}
          action={slotFull ? 'Velg et annet tidspunkt' : 'Endre tid for timen'}
          onAbort={handleAbort}
          onConfirm={slotFull ? handleAbort : handleConfirm}
          show={confirm}
          loading={loading}
          loadingMessage="Endrer time"
          warning={slotFull}
          disabled={loading}
        >
          {slotFull ? (
            <>
              <strong>{selectedDate}</strong> ble plutselig fullt. Vennligst velg et annet tidspunkt
              og prøv igjen.
            </>
          ) : (
            <>
              {location?.title || 'Teststasjon uten navn'}
              <br />
              For {fullName}
              {bookingError ? (
                <div>
                  <br />
                  <ErrorMessage error={bookingError} />
                </div>
              ) : null}
            </>
          )}
        </Confirm>
      </Step>
    </Wrapper>
  )
}

const Wrapper = ({ children }) => (
  <>
    <Header title="Endre time" />
    <Container>{children}</Container>
  </>
)
Wrapper.propTypes = {
  children: T.object.isRequired,
  size: T.string,
}

EditBooking.propTypes = {
  match: T.object.isRequired,
}
